import { Flex, Text } from '@backstage/ui';
import type {
  CheckRule,
  CheckRuleCondition,
} from '@internal/backstage-plugin-pulse-common';

const isCondition = (
  item: CheckRule | CheckRuleCondition,
): item is CheckRuleCondition => 'factRef' in item;

const serializeValue = (value: unknown): string => {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') return value;
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value.toString();
  }
  if (typeof value === 'symbol') return value.description ?? value.toString();
  return JSON.stringify(value) ?? '[object]';
};

const getRuleKey = (
  rule: CheckRule | CheckRuleCondition,
  index: number,
  depth: number,
): string => {
  if (isCondition(rule)) {
    return `${depth}-cond-${index}-${rule.factRef}:${rule.path}`;
  }
  const groupType = 'any' in rule ? 'any' : 'all';
  return `${depth}-${groupType}-${index}`;
};

type CheckRuleNodeProps = {
  rule: CheckRule | CheckRuleCondition;
  depth?: number;
};

const CheckRuleNode = ({ rule, depth = 0 }: CheckRuleNodeProps) => {
  const indent = depth * 12;

  if (isCondition(rule)) {
    return (
      <Flex
        align="center"
        gap="2"
        style={{ paddingLeft: `${indent}px`, flexWrap: 'wrap' }}
      >
        <Text
          variant="body-small"
          color="secondary"
          style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {rule.path}
        </Text>
        <Text variant="body-small">{rule.operator}</Text>
        {rule.value !== undefined && (
          <Text
            variant="body-small"
            color="secondary"
            style={{ fontFamily: 'monospace' }}
          >
            {serializeValue(rule.value)}
          </Text>
        )}
      </Flex>
    );
  }

  const groupType = 'any' in rule ? 'ANY' : 'ALL';
  const children: (CheckRule | CheckRuleCondition)[] =
    rule.any ?? rule.all ?? [];

  return (
    <Flex direction="column" gap="1">
      <Text
        variant="body-small"
        style={{
          paddingLeft: `${indent}px`,
          color: 'var(--bui-fg-tertiary)',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        {groupType}
      </Text>
      {children.map((child, index) => (
        <CheckRuleNode
          key={getRuleKey(child, index, depth + 1)}
          rule={child}
          depth={depth + 1}
        />
      ))}
    </Flex>
  );
};

type CheckRuleTreeProps = {
  rule: CheckRule;
};

/** Renders a `CheckRule` as a structured tree showing `any`/`all` groups and
 * their conditions (fact path, operator, expected value). */
export const CheckRuleTree = ({ rule }: CheckRuleTreeProps) => (
  <Flex direction="column" gap="1">
    <CheckRuleNode rule={rule} />
  </Flex>
);
